// Таб «Сканер» в Меню: фото → распознавание → проверка человеком → импорт.
// Фаза 1 (scan) ничего не пишет в БД, возвращает черновик. Здесь человек правит,
// затем фаза 2 (import) разворачивает варианты в плоские блюда и пишет bulk-ом.
// Контракт — services/menuScanner.ts (зеркало backend/src/modules/menu-scan).
import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Camera, Image as ImageIcon, ScanLine, Loader2, Trash2, Plus, AlertTriangle, RotateCcw } from 'lucide-react';
import toast from 'react-hot-toast';

import { Spinner } from '@/components/ui/Spinner';
import { listCategories, type Category } from '@/services/admin';
import { compressImageForUpload } from '@/utils/image';
import {
  scanMenu, importMenu,
  type ScanResult, type ImportDish, type ImportModifier,
} from '@/services/menuScanner';

// ── редактируемые модели (строки в UI). Цены — строки, чтобы свободно править ──
type VRow = { key: string; label: string; price: string; isRange: boolean; note?: string; on: boolean };
type DRow = {
  tempId: string;
  name: string;
  categoryId: string; // '' = создать новую категорию по newName
  newName: string;
  confidence: number;
  variants: VRow[];
};
type MRow = { tempId: string; name: string; price: string; note?: string; on: boolean };

function parsePrice(s: string): number | null {
  const n = Number(String(s).replace(',', '.').replace(/\s/g, ''));
  return Number.isFinite(n) && n >= 0 ? n : null;
}

// scan-результат → редактируемые строки. seq делает tempId уникальными между фото.
function toRows(res: ScanResult, seq: number): { rows: DRow[]; mods: MRow[] } {
  const rows: DRow[] = res.items.map((it) => ({
    tempId: `${seq}_${it.tempId}`,
    name: it.name,
    categoryId: it.categoryId ?? '',
    newName: it.category,
    confidence: it.confidence,
    variants: it.variants.map((v, i) => {
      const base = v.priceRub ?? v.priceMaxRub;
      return {
        key: `${seq}_${it.tempId}_${i}`,
        label: v.label,
        price: base != null ? String(base) : '',
        isRange: v.priceMaxRub != null,
        note: v.note ?? (v.priceMaxRub != null && v.priceRub != null ? `диапазон ${v.priceRub}–${v.priceMaxRub} ₽` : undefined),
        on: base != null,
      };
    }),
  }));
  const mods: MRow[] = res.modifiers.map((m) => ({
    tempId: `${seq}_${m.tempId}`,
    name: m.name,
    price: String(m.priceRub),
    note: m.note,
    on: true,
  }));
  return { rows, mods };
}

export default function MenuScannerTab() {
  const qc = useQueryClient();
  const cameraRef = useRef<HTMLInputElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const seqRef = useRef(0);

  const [rows, setRows] = useState<DRow[]>([]);
  const [mods, setMods] = useState<MRow[]>([]);
  const [warnings, setWarnings] = useState<string[]>([]);
  const reviewing = rows.length > 0 || mods.length > 0;

  const { data: categories = [] } = useQuery({ queryKey: ['admin/cat'], queryFn: listCategories });

  const scanMut = useMutation({
    mutationFn: async (file: File) => scanMenu(await compressImageForUpload(file)),
    onSuccess: (res: ScanResult) => {
      const seq = ++seqRef.current;
      const { rows: r, mods: m } = toRows(res, seq);
      setRows((prev) => [...prev, ...r]);
      setMods((prev) => [...prev, ...m]);
      setWarnings((prev) => [...prev, ...res.warnings]);
      if (!r.length && !m.length) toast('На фото не нашлось позиций');
      else toast.success(`Распознано: ${r.length} блюд${m.length ? `, ${m.length} модификаторов` : ''}`);
    },
    // ошибки (вкл. 503 «Сканер выключен») показывает глобальный перехватчик axios в api.ts
  });

  const importMut = useMutation({
    mutationFn: () => {
      const dishes: ImportDish[] = [];
      for (const d of rows) {
        const dishName = d.name.trim();
        for (const v of d.variants) {
          if (!v.on) continue;
          const priceRub = parsePrice(v.price);
          if (priceRub == null) continue;
          const label = v.label.trim();
          const name = label ? `${dishName}, ${label}` : dishName;
          if (!name) continue;
          const item: ImportDish = { name, priceRub };
          if (d.categoryId) item.categoryId = d.categoryId;
          else item.categoryName = (d.newName.trim() || dishName);
          if (label) item.weight = label;
          dishes.push(item);
        }
      }
      const modifiers: ImportModifier[] = [];
      for (const m of mods) {
        if (!m.on) continue;
        const priceRub = parsePrice(m.price);
        const name = m.name.trim();
        if (priceRub == null || !name) continue;
        modifiers.push({ name, priceRub });
      }
      if (!dishes.length && !modifiers.length) {
        return Promise.reject(new Error('Нечего импортировать — отметьте хотя бы одну позицию с ценой'));
      }
      return importMenu(dishes, modifiers, true);
    },
    onSuccess: (res) => {
      toast.success(
        `Импорт: +${res.createdDishes} блюд, +${res.createdCategories} категорий` +
        (res.createdModifiers ? `, +${res.createdModifiers} модификаторов` : '') +
        (res.skipped ? ` · пропущено ${res.skipped}` : ''),
      );
      qc.invalidateQueries({ queryKey: ['admin/dishes'] });
      qc.invalidateQueries({ queryKey: ['admin/cat'] });
      qc.invalidateQueries({ queryKey: ['admin/mods'] });
      reset();
    },
    onError: (e: unknown) => {
      if (e instanceof Error && e.message.startsWith('Нечего')) toast.error(e.message);
    },
  });

  const reset = () => {
    setRows([]); setMods([]); setWarnings([]);
    if (cameraRef.current) cameraRef.current.value = '';
    if (fileRef.current) fileRef.current.value = '';
  };

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Нужно изображение'); return; }
    scanMut.mutate(file);
  };

  // ── мутаторы строк ──
  const patchDish = (tempId: string, patch: Partial<DRow>) =>
    setRows((prev) => prev.map((d) => (d.tempId === tempId ? { ...d, ...patch } : d)));
  const patchVar = (tempId: string, key: string, patch: Partial<VRow>) =>
    setRows((prev) => prev.map((d) => d.tempId !== tempId ? d : {
      ...d, variants: d.variants.map((v) => (v.key === key ? { ...v, ...patch } : v)),
    }));
  const removeVar = (tempId: string, key: string) =>
    setRows((prev) => prev.map((d) => d.tempId !== tempId ? d : {
      ...d, variants: d.variants.filter((v) => v.key !== key),
    }).filter((d) => d.variants.length > 0));
  const addVar = (tempId: string) =>
    setRows((prev) => prev.map((d) => d.tempId !== tempId ? d : {
      ...d, variants: [...d.variants, { key: `${d.tempId}_n${d.variants.length}_${Date.now()}`, label: '', price: '', isRange: false, on: true }],
    }));
  const removeDish = (tempId: string) => setRows((prev) => prev.filter((d) => d.tempId !== tempId));

  const includedDishes = rows.reduce(
    (n, d) => n + d.variants.filter((v) => v.on && parsePrice(v.price) != null).length, 0,
  );
  const includedMods = mods.filter((m) => m.on && parsePrice(m.price) != null && m.name.trim()).length;

  const busy = scanMut.isPending;

  return (
    <div className="grid gap-4">
      <CaptureBar
        reviewing={reviewing}
        busy={busy}
        onCamera={() => cameraRef.current?.click()}
        onFile={() => fileRef.current?.click()}
      />

      {warnings.length > 0 && (
        <div className="card p-3 border-amber-200 bg-amber-50 text-amber-800 text-sm flex gap-2">
          <AlertTriangle size={16} className="shrink-0 mt-0.5" />
          <ul className="list-disc pl-4 space-y-0.5">{warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
        </div>
      )}

      {busy && !reviewing && (
        <div className="card p-8 grid place-items-center gap-3 text-slate-500">
          <Spinner />
          <span className="text-sm">Распознаём меню…</span>
        </div>
      )}

      {!busy && !reviewing && <EmptyState />}

      {reviewing && (
        <>
          <div className="grid gap-3">
            {rows.map((d) => (
              <DishCard
                key={d.tempId}
                dish={d}
                categories={categories}
                onPatch={(p) => patchDish(d.tempId, p)}
                onPatchVar={(key, p) => patchVar(d.tempId, key, p)}
                onRemoveVar={(key) => removeVar(d.tempId, key)}
                onAddVar={() => addVar(d.tempId)}
                onRemove={() => removeDish(d.tempId)}
              />
            ))}
          </div>

          <ModifiersBlock mods={mods} setMods={setMods} />

          <div className="sticky bottom-0 -mx-4 sm:mx-0 px-4 sm:px-0 py-3 bg-gradient-to-t from-white via-white to-white/0">
            <div className="card p-3 flex flex-wrap items-center gap-3">
              <div className="text-sm text-slate-600">
                К импорту: <b>{includedDishes}</b> блюд{includedMods ? <>, <b>{includedMods}</b> модификаторов</> : null}
              </div>
              <div className="flex gap-2 ml-auto">
                <button className="btn-ghost" onClick={reset} disabled={importMut.isPending}>
                  <RotateCcw size={16} /> Сбросить
                </button>
                <button
                  className="btn-primary"
                  onClick={() => importMut.mutate()}
                  disabled={importMut.isPending || (includedDishes === 0 && includedMods === 0)}
                >
                  {importMut.isPending ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
                  Импортировать
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onPick} />
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPick} />
    </div>
  );
}

function CaptureBar({ reviewing, busy, onCamera, onFile }: {
  reviewing: boolean; busy: boolean; onCamera: () => void; onFile: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <button className="btn-primary" onClick={onCamera} disabled={busy}>
        {busy ? <Loader2 size={16} className="animate-spin" /> : <Camera size={16} />}
        {reviewing ? 'Ещё фото (камера)' : 'Сфотографировать меню'}
      </button>
      <button className="btn-outline" onClick={onFile} disabled={busy}>
        <ImageIcon size={16} /> {reviewing ? 'Ещё из файлов' : 'Загрузить файл'}
      </button>
      {reviewing && <span className="text-xs text-slate-400 ml-auto">Проверьте и поправьте перед импортом</span>}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="card p-8 grid place-items-center gap-2 text-center">
      <div className="w-12 h-12 rounded-xl bg-brand-50 text-brand-600 grid place-items-center">
        <ScanLine size={24} />
      </div>
      <div className="font-medium">Сканер меню</div>
      <p className="text-sm text-slate-500 max-w-md">
        Сфотографируйте бумажное меню — распознаем категории, блюда и цены.
        Перед записью всё можно проверить и поправить.
      </p>
    </div>
  );
}

function confColor(c: number): string {
  if (c >= 0.8) return 'bg-emerald-500';
  if (c >= 0.6) return 'bg-amber-500';
  return 'bg-rose-500';
}

function DishCard({ dish, categories, onPatch, onPatchVar, onRemoveVar, onAddVar, onRemove }: {
  dish: DRow;
  categories: Category[];
  onPatch: (p: Partial<DRow>) => void;
  onPatchVar: (key: string, p: Partial<VRow>) => void;
  onRemoveVar: (key: string) => void;
  onAddVar: () => void;
  onRemove: () => void;
}) {
  const lowConf = dish.confidence < 0.6;
  return (
    <div className={`card p-3 grid gap-2 ${lowConf ? 'ring-1 ring-amber-300' : ''}`}>
      <div className="flex items-center gap-2">
        <span title={`Уверенность ${Math.round(dish.confidence * 100)}%`} className={`w-2 h-2 rounded-full shrink-0 ${confColor(dish.confidence)}`} />
        <input
          className="input flex-1 min-w-0 font-medium"
          value={dish.name}
          placeholder="Название блюда"
          onChange={(e) => onPatch({ name: e.target.value })}
        />
        <button className="btn-ghost text-red-600 shrink-0" title="Удалить блюдо" onClick={onRemove}>
          <Trash2 size={16} />
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <select
          className="input w-full sm:w-56"
          value={dish.categoryId}
          onChange={(e) => onPatch({ categoryId: e.target.value })}
        >
          <option value="">➕ Новая категория…</option>
          {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        {dish.categoryId === '' && (
          <input
            className="input flex-1 min-w-[160px]"
            value={dish.newName}
            placeholder="Имя новой категории"
            onChange={(e) => onPatch({ newName: e.target.value })}
          />
        )}
      </div>

      <div className="grid gap-1.5">
        {dish.variants.map((v) => {
          const bad = v.on && parsePrice(v.price) == null;
          return (
            <div key={v.key} className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={v.on}
                onChange={(e) => onPatchVar(v.key, { on: e.target.checked })}
                title="Включить в импорт"
              />
              <input
                className="input w-24 sm:w-28"
                value={v.label}
                placeholder="размер"
                onChange={(e) => onPatchVar(v.key, { label: e.target.value })}
              />
              <div className="relative">
                <input
                  className={`input w-28 pr-6 ${bad ? 'border-rose-400' : ''}`}
                  inputMode="decimal"
                  value={v.price}
                  placeholder="0"
                  onChange={(e) => onPatchVar(v.key, { price: e.target.value })}
                />
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 text-sm">₽</span>
              </div>
              {v.note && <span className="text-xs text-amber-600 truncate max-w-[160px]" title={v.note}>{v.note}</span>}
              <button className="btn-ghost text-slate-400 hover:text-red-600 ml-auto shrink-0" title="Убрать вариант" onClick={() => onRemoveVar(v.key)}>
                <Trash2 size={14} />
              </button>
            </div>
          );
        })}
        <button className="btn-ghost text-xs justify-self-start text-brand-600" onClick={onAddVar}>
          <Plus size={14} /> Вариант
        </button>
      </div>
    </div>
  );
}

function ModifiersBlock({ mods, setMods }: { mods: MRow[]; setMods: React.Dispatch<React.SetStateAction<MRow[]>> }) {
  const patch = (tempId: string, p: Partial<MRow>) =>
    setMods((prev) => prev.map((m) => (m.tempId === tempId ? { ...m, ...p } : m)));
  const remove = (tempId: string) => setMods((prev) => prev.filter((m) => m.tempId !== tempId));
  const add = () =>
    setMods((prev) => [...prev, { tempId: `mnew_${Date.now()}`, name: '', price: '', on: true }]);

  return (
    <div className="card p-3 grid gap-2">
      <div className="flex items-center justify-between">
        <div className="text-sm font-medium">Модификаторы / добавки</div>
        <button className="btn-ghost text-xs text-brand-600" onClick={add}><Plus size={14} /> Добавить</button>
      </div>
      {mods.length === 0 && <div className="text-xs text-slate-400">Не распознаны. Можно добавить вручную.</div>}
      {mods.map((m) => {
        const bad = m.on && parsePrice(m.price) == null;
        return (
          <div key={m.tempId} className="flex items-center gap-2">
            <input type="checkbox" checked={m.on} onChange={(e) => patch(m.tempId, { on: e.target.checked })} />
            <input className="input flex-1 min-w-0" value={m.name} placeholder="Название" onChange={(e) => patch(m.tempId, { name: e.target.value })} />
            <div className="relative">
              <input
                className={`input w-28 pr-6 ${bad ? 'border-rose-400' : ''}`}
                inputMode="decimal"
                value={m.price}
                placeholder="0"
                onChange={(e) => patch(m.tempId, { price: e.target.value })}
              />
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 text-sm">₽</span>
            </div>
            <button className="btn-ghost text-slate-400 hover:text-red-600 shrink-0" onClick={() => remove(m.tempId)}>
              <Trash2 size={14} />
            </button>
          </div>
        );
      })}
      <p className="text-xs text-slate-400">Создаются на уровне заведения. Привязать к блюдам — во вкладке «Блюда».</p>
    </div>
  );
}
